import { useState } from 'react';
import type { ApiKeyDtoT } from '@wave/shared';
import { useApiKeys, useRevokeApiKey } from '../../api/integration';
import {
  btnPrimary,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from '../../lib/styles';
import { ApiKeyModal } from './ApiKeyModal';
import { SCOPE_LABELS } from './labels';

/**
 * Вкладка «Ключи» страницы /admin/api — CRUD API-ключей публичного API приёма
 * заявок (право integrations.manage, только admin). Revoke необратим — ключ
 * без действий в таблице после отзыва.
 */
export function ApiKeysTab() {
  const { data: keys, isLoading, error } = useApiKeys();
  const revoke = useRevokeApiKey();
  const [modal, setModal] = useState<ApiKeyDtoT | 'new' | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text2">
          Ключи для внешних сервисов, создающих заявки через публичный API.
        </div>
        <button onClick={() => setModal('new')} className={btnPrimary}>
          + Новый ключ
        </button>
      </div>

      {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
      {error && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {keys && (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Имя</th>
                <th className={tableHeadCell}>Ключ</th>
                <th className={tableHeadCell}>Скоупы</th>
                <th className={tableHeadCell}>Webhook</th>
                <th className={tableHeadCell}>Использован</th>
                <th className={tableHeadCell}>Статус</th>
                <th className={`${tableHeadCell} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center text-sm text-text2">
                    Ключей пока нет — создайте первый для внешней интеграции.
                  </td>
                </tr>
              )}
              {keys.map((k) => (
                <KeyRow
                  key={k.id}
                  apiKey={k}
                  onEdit={() => setModal(k)}
                  onRevoke={() => {
                    if (confirm(`Отозвать ключ «${k.name}»? Действие необратимо.`)) {
                      void revoke.mutateAsync(k.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <ApiKeyModal apiKey={modal === 'new' ? null : modal} onClose={() => setModal(null)} />}
    </div>
  );
}

function KeyRow({
  apiKey: k,
  onEdit,
  onRevoke,
}: {
  apiKey: ApiKeyDtoT;
  onEdit: () => void;
  onRevoke: () => void;
}) {
  const isRevoked = !!k.revokedAt;

  return (
    <tr className={tableRow}>
      <td className={`${tableCell} font-medium text-text`}>{k.name}</td>
      <td className={tableCell}>
        <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-xs text-text2">{k.keyPrefix}…</code>
      </td>
      <td className={tableCell}>
        <div className="flex flex-wrap gap-1">
          {k.scopes.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded border border-[var(--accent-border)] bg-[var(--accent-bg)] px-1.5 py-0.5 text-[11px] font-medium text-accent"
            >
              {SCOPE_LABELS[s]}
            </span>
          ))}
        </div>
      </td>
      <td className={tableCell}>
        {k.webhookUrl ? (
          <span className="text-success" title={k.webhookUrl}>
            ✓
          </span>
        ) : (
          <span className="text-text-subtle">—</span>
        )}
      </td>
      <td className={`${tableCell} text-text2`}>
        {k.lastUsedAt ? formatDateTime(k.lastUsedAt) : '—'}
      </td>
      <td className={tableCell}>
        {isRevoked ? (
          <span className="text-danger">Отозван</span>
        ) : k.enabled ? (
          <span className="text-success">Вкл</span>
        ) : (
          <span className="text-text2">Выкл</span>
        )}
      </td>
      <td className={`${tableCell} text-right`}>
        {!isRevoked && (
          <>
            <button
              onClick={onEdit}
              className="mr-3 text-xs font-medium text-text2 underline-offset-4 transition-colors duration-150 hover:text-text hover:underline"
            >
              Изменить
            </button>
            <button
              onClick={onRevoke}
              className="text-xs font-medium text-danger underline-offset-4 transition-colors duration-150 hover:underline"
            >
              Отозвать
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', { hour12: false });
}
